/**
 * LoadingSkeleton — generic skeleton para reemplazar el "Cargando…" plano
 * que aparecía en 21 lugares de la app.
 *
 * Variants:
 *  - default: 3 líneas de texto + 1 chart bar (página genérica)
 *  - rows:    N filas (tabla / list)
 *  - card:    1 card con 2 líneas (tile / summary card)
 *
 * Usa `animate-pulse` (Tailwind built-in). aria-busy + aria-live polite para
 * screen readers.
 *
 * Nota: para páginas con layout muy específico (Industry jobs, Planetary
 * colonies) seguimos usando skeletons dedicados — el genérico es fallback
 * para "todos los demás".
 */

interface Props {
  variant?: 'default' | 'rows' | 'card'
  rows?: number
}

export function LoadingSkeleton({ variant = 'default', rows = 4 }: Props) {
  if (variant === 'card') {
    return (
      <div
        className="bg-eve-bg border border-eve-border rounded-md p-3 space-y-2"
        aria-busy="true"
        aria-live="polite"
      >
        <div className="h-3 w-1/3 bg-eve-border/40 rounded animate-pulse" />
        <div className="h-5 w-2/3 bg-eve-border/40 rounded animate-pulse" />
      </div>
    )
  }

  if (variant === 'rows') {
    return (
      <div className="space-y-2" aria-busy="true" aria-live="polite">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-2 py-2"
          >
            <div className="h-3 w-1/4 bg-eve-border/40 rounded animate-pulse" />
            <div className="flex-1 h-3 bg-eve-border/30 rounded animate-pulse" />
            <div className="h-3 w-16 bg-eve-border/40 rounded animate-pulse" />
          </div>
        ))}
      </div>
    )
  }

  // default
  return (
    <div className="space-y-3" aria-busy="true" aria-live="polite">
      <div className="bg-eve-bg border border-eve-border rounded-md h-20 flex items-end gap-1 p-3">
        {[0.3, 0.6, 0.45, 0.75, 0.5, 0.65].map((h, i) => (
          <div
            key={i}
            className="flex-1 bg-eve-border/40 rounded-t animate-pulse"
            style={{ height: `${h * 100}%` }}
          />
        ))}
      </div>
      <div className="space-y-2">
        <div className="h-3 w-3/4 bg-eve-border/40 rounded animate-pulse" />
        <div className="h-3 w-2/3 bg-eve-border/40 rounded animate-pulse" />
        <div className="h-3 w-1/2 bg-eve-border/30 rounded animate-pulse" />
      </div>
    </div>
  )
}
